import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface CsatConfig {
  isEnabled: boolean;
  surveyMessage: string;
  delaySeconds: number;
  ratingType: "stars" | "thumbs" | "emoji";
  allowComment: boolean;
  commentPlaceholder: string;
  thankYouMessage: string;
}

const RATING_TYPES = [
  { value: "stars", label: "⭐ Stars (1–5)", desc: "Classic 5-star rating" },
  { value: "thumbs", label: "👍 Thumbs Up / Down", desc: "Simple positive or negative" },
  { value: "emoji", label: "😊 Emoji Scale", desc: "Bad / Neutral / Great — 3 options" },
];

const DELAY_OPTIONS = [
  { value: 0,   label: "Immediately" },
  { value: 30,  label: "After 30 seconds" },
  { value: 60,  label: "After 1 minute" },
  { value: 300, label: "After 5 minutes" },
];

export default function CsatSettingsPanel() {
  const api = useApi();
  const [config, setConfig] = useState<CsatConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    api.admin.get("Csat/Config").then(async (res) => {
      if (res.ok) setConfig(await res.json());
    }).catch(() => {});
  }, [api.admin]);

  async function save() {
    if (!config) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.admin.put("Csat/Config", config);
      setSaveMsg(res.ok ? "Saved!" : "Failed to save.");
      setTimeout(() => setSaveMsg(null), 3000);
    } finally {
      setSaving(false);
    }
  }

  if (!config) {
    return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Customer Satisfaction (CSAT)</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Automatically survey visitors after conversations resolve. Track satisfaction scores over time.
          </p>
        </div>
        <Link
          to="/reports/csat"
          className="text-sm text-primary hover:text-cornflower-blue-800 border border-cornflower-blue-200 rounded-lg px-3 py-1.5 transition-colors shrink-0 ml-4"
        >
          View results →
        </Link>
      </div>

      <div className="space-y-6">
        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
          <div>
            <p className="text-sm font-medium text-slate-800">Enable CSAT surveys</p>
            <p className="text-xs text-slate-500 mt-0.5">
              A survey message is sent to the visitor after every resolved conversation.
            </p>
          </div>
          <button
            role="switch"
            aria-checked={config.isEnabled}
            onClick={() => setConfig(c => c ? { ...c, isEnabled: !c.isEnabled } : c)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.isEnabled ? "bg-blue-600" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${config.isEnabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        {config.isEnabled && (
          <>
            {/* Rating type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Rating style</label>
              <div className="grid grid-cols-3 gap-3">
                {RATING_TYPES.map(rt => (
                  <button
                    key={rt.value}
                    onClick={() => setConfig(c => c ? { ...c, ratingType: rt.value as CsatConfig["ratingType"] } : c)}
                    className={`p-3 text-left border rounded-xl transition-all ${config.ratingType === rt.value
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <p className="text-sm font-medium text-slate-800">{rt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{rt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Survey message */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Survey message</label>
              <textarea
                value={config.surveyMessage}
                onChange={e => setConfig(c => c ? { ...c, surveyMessage: e.target.value } : c)}
                rows={2}
                maxLength={500}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                Shown to the visitor as the first survey message. Supports <code className="bg-slate-100 px-1 rounded">{"<<contact_name>>"}</code>.
              </p>
            </div>

            {/* Send delay */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Send timing</label>
              <select
                value={config.delaySeconds}
                onChange={e => setConfig(c => c ? { ...c, delaySeconds: Number(e.target.value) } : c)}
                className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              >
                {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Time after conversation resolves before the survey is sent.</p>
            </div>

            {/* Comment toggle */}
            <div className="flex items-center gap-3">
              <button
                role="switch"
                aria-checked={config.allowComment}
                onClick={() => setConfig(c => c ? { ...c, allowComment: !c.allowComment } : c)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${config.allowComment ? "bg-blue-600" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.allowComment ? "translate-x-4.5" : "translate-x-0.5"}`} />
              </button>
              <div>
                <p className="text-sm font-medium text-slate-700">Allow written comments</p>
                <p className="text-xs text-slate-500">After rating, offer a free-text comment box.</p>
              </div>
            </div>

            {config.allowComment && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comment placeholder</label>
                <input
                  type="text"
                  value={config.commentPlaceholder}
                  onChange={e => setConfig(c => c ? { ...c, commentPlaceholder: e.target.value } : c)}
                  maxLength={200}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
            )}

            {/* Thank you message */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Thank-you message</label>
              <input
                type="text"
                value={config.thankYouMessage}
                onChange={e => setConfig(c => c ? { ...c, thankYouMessage: e.target.value } : c)}
                maxLength={300}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">Shown after the visitor submits their rating.</p>
            </div>
          </>
        )}

        {/* Save */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
          {saveMsg && (
            <span className={`text-sm ${saveMsg === "Saved!" ? "text-violet-600" : "text-rose-600"}`}>
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
